import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, Inject } from '@angular/core';
import { Subscription, interval, of, takeWhile } from 'rxjs';
import { UserService } from '../../services/user services/user.service';
import { FirestoreService } from '../../services/admin services/firestore.service';
import { Timestamp } from 'firebase/firestore';
import { StorageService } from '../../services/admin services/storage.service';
import { SessionManagementService } from '../../services/admin services/session-management.service';
import { Share } from '@capacitor/share';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoDetailModalComponent } from '../photo-detail-modal/photo-detail-modal.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnonymousUserService } from '../../services/user services/anonymous-user.service';


@Component({
  selector: 'app-video-detail-modal',
  templateUrl: './video-detail-modal.component.html',
  styleUrls: ['./video-detail-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ]
})

export class VideoDetailModalComponent implements OnInit, OnDestroy {




  isPublisher: boolean = false;
  expiresIn: number = 24 * 60 * 60 * 1000;
  videoId: string = '';
  videoUrl: string = '';
  deviceId: string = '';
  timeRemaining: string = '';
  countdown: number = 0;
  likeStatus: 'none' | 'liked' | 'disliked' = 'none';
  isImageLoading: boolean = true;
  likeCount: number = 0;
  dislikeCount: number = 0;
  userId!: string;
  expirationDuration: number = 24 * 60 * 60 * 1000;
  videoTimestamp: Timestamp | null = null;
  priorityAdjustment: number = 0;
  timeAdjustment: number = 0;
  publisheruserId!: string;
  lowQualityUrl: string | null = null;
  highQualityUrl: string | null = null;
  isLowQualityLoaded: boolean = false;
  isHighQualityLoaded: boolean = false;
  hasInteractedOnce: boolean = false;
  private readonly storageKeyPrefix = 'video_interaction_';

  private countdownSubscription: Subscription | null = null;
  private timerInterval: any = null;

  private timerSubscription?: Subscription;
  private readonly maxVideoDuration: number = 30; // 30 seconds max for reels

  constructor(
    private _firestoreService: FirestoreService,
    private _anonymousUserService: AnonymousUserService,
    private _storageService: StorageService,
    public dialogRef: MatDialogRef<PhotoDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _sessionManagementService: SessionManagementService
  ) { }

  ngOnInit(): void {
    // Extract data from MAT_DIALOG_DATA injection
    if (this.data) {
      this.videoId = this.data.videoId || '';
      this.videoUrl = this.data.videoUrl || '';
      this.deviceId = this.data.deviceId || '';
      this.isPublisher = this.data.isPublisher || false;
      this.userId = this.data.userId || '';
      this.expiresIn = this.data.expiresIn || (24 * 60 * 60 * 1000);
    }

    // Get current user ID (fallback if not provided)
    if (!this.userId) {
      this.userId = this._anonymousUserService.currentUserId || '';
    }

    console.log('[VideoDetailModal] ngOnInit called with extracted data:', {
      videoId: this.videoId,
      videoUrl: this.videoUrl,
      deviceId: this.deviceId,
      isPublisher: this.isPublisher,
      userId: this.userId
    });

    this.refreshVideoData();
    this.verifyPublisherStatus();
    this.initializeLikeStatus();
  }

  private async verifyPublisherStatus() {
    // console.log('[videoDetailModal] Verifying publisher status...');

    if (!this.videoId) {
      console.warn('[videoDetailModal] Missing videoId, cannot verify publisher status');
      return;
    }

    try {
      // Get video data from Firestore
      const videoData = await this._firestoreService.getVideoById(this.videoId);

      // Get device ID from IndexedDB as an additional check
      let idbDeviceId = '';
      try {
        idbDeviceId = await this.getDeviceIdFromIDB();
      } catch (idbError) {
        console.error('[videoDetailModal] Error getting device ID from IndexedDB:', idbError);
        // Continue with the check using the provided deviceId
      }

      // Check all possible matches to determine if user is publisher
      const actualIsPublisher: boolean = !!(
        // Check against provided deviceId
        videoData.userId === this.deviceId ||
        videoData.deviceId === this.deviceId ||
        // Check against IndexedDB deviceId if available
        (idbDeviceId && (videoData.userId === idbDeviceId || videoData.deviceId === idbDeviceId))
      );

      // console.log('[videoDetailModal] Publisher check results:', {
      //   receivedIsPublisher: this.isPublisher,
      //   actualIsPublisher: actualIsPublisher,
      //   videoUserId: videoData.userId,
      //   videoDeviceId: videoData.deviceId,
      //   inputDeviceId: this.deviceId,
      //   idbDeviceId: idbDeviceId
      // });

      // Update the isPublisher flag based on actual data
      if (this.isPublisher !== actualIsPublisher) {
        console.warn(
          '[videoDetailModal] Correcting isPublisher flag!',
          'Received:', this.isPublisher,
          'Actual:', actualIsPublisher
        );
        this.isPublisher = actualIsPublisher;
      }

    } catch (error) {
      console.error('[videoDetailModal] Error verifying publisher status:', error);
    }
  }

  private async getDeviceIdFromIDB(): Promise<string> {
    //console.log('[videoDetailModal] Attempting to get device ID from IndexedDB');
    return new Promise((resolve, reject) => {
      //console.log('[videoDetailModal] Opening user_database');
      const request = indexedDB.open('user_database', 1);

      request.onupgradeneeded = (event) => {
        //  console.log('[videoDetailModal] Database upgrade needed event triggered');
      };

      request.onerror = (event) => {
        console.error('[videoDetailModal] Error opening IndexedDB:', event);
        console.error('[videoDetailModal] Error details:', (event.target as any).error);
        reject('Error opening database');
      };

      request.onsuccess = (event) => {
        // console.log('[videoDetailModal] Database opened successfully');
        const db = request.result;

        // Log available object stores
        // console.log('[videoDetailModal] Available object stores:', Array.from(db.objectStoreNames));

        try {
          // console.log('[videoDetailModal] Creating transaction for user_store');
          const transaction = db.transaction(['user_store'], 'readonly');

          transaction.onerror = (event) => {
            console.error('[videoDetailModal] Transaction error:', event);
            reject('Transaction error');
          };

          //console.log('[videoDetailModal] Getting user_store object store');
          const store = transaction.objectStore('user_store');

          // console.log('[videoDetailModal] Requesting device_id from store');
          // Get the 'device_id' record which contains a key-value pair
          const getRequest = store.get('device_id');

          getRequest.onerror = (event) => {
            console.error('[PhotoDetailModal] Error reading from IndexedDB:', event);
            reject('Error reading from database');
          };

          getRequest.onsuccess = (event) => {
            // console.log('[PhotoDetailModal] Get request completed successfully');
            const result = getRequest.result;
            // console.log('[PhotoDetailModal] Raw result:', result);

            // The IDB format is {key: 'device_id', value: 'ObnoxiousSheep5'}
            if (result && result.value) {
              // console.log('[PhotoDetailModal] Device ID from IndexedDB:', result.value);
              resolve(result.value);
            } else {
              console.warn('[PhotoDetailModal] Device ID not found in IndexedDB');
              resolve('');
            }
          };
        } catch (error) {
          console.error('[PhotoDetailModal] Error in IndexedDB operations:', error);
          reject(`Error in IndexedDB operations: ${error}`);
        }
      };
    });
  }

  async initializeLikeStatus() {
    try {
      if (!this.userId) {
        this.userId = this._anonymousUserService.currentUserId || '';
      }

      // Fetch the video data from Firestore
      const videoData = await this._firestoreService.getVideoById(this.videoId);
      if (videoData) {
        const likes = videoData.likes || [];
        const dislikes = videoData.dislikes || [];

        // Set counts
        this.likeCount = likes.length;
        this.dislikeCount = dislikes.length;

        // Set user's status
        if (likes.includes(this.userId)) {
          this.likeStatus = 'liked';
          this.hasInteractedOnce = true;
        } else if (dislikes.includes(this.userId)) {
          this.likeStatus = 'disliked';
          this.hasInteractedOnce = true;
        } else {
          this.likeStatus = 'none';
          // Check local storage for previous interactions
          this.hasInteractedOnce = this.loadInteractionState();
        }

        // Save the current state to local storage if user has interacted
        if (this.hasInteractedOnce) {
          this.saveInteractionState();
        }
      }
    } catch (error) {
      console.error('[PhotoDetailModal] Error initializing like status:', error);
    }
  }

  private loadInteractionState(): boolean {
    const storedState = localStorage.getItem(this.getStorageKey());
    if (storedState && ['liked', 'disliked', 'none'].includes(storedState)) {
      return true;
    }
    return false;
  }

  async likeVideo() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing like photo logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[videoDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.likeVideo(this.videoId, uid);

      if (previousStatus === 'liked') {
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        this.likeCount++;
        if (previousStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }

      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshVideoData();
    } catch (error) {
      console.error('[VideoDetailModal] Error liking video:', error);
    }
  }

  async dislikeVideo() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing dislike video logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[VideoDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.dislikeVideo(this.videoId, uid);

      if (previousStatus === 'disliked') {
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        this.dislikeCount++;
        if (previousStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }

      // Set interaction flag to true and save to local storage
      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshVideoData();
    } catch (error) {
      console.error('[VideoDetailModal] Error disliking video:', error);
    }
  }

  private saveInteractionState(): void {
    if (this.hasInteractedOnce) {
      localStorage.setItem(this.getStorageKey(), this.likeStatus);
    }
  }

  private getStorageKey(): string {
    const uid = this._anonymousUserService.currentUserId;
    return `${this.storageKeyPrefix}${this.videoId}_${uid}`;
  }

  async downloadVideoByThirdParty() {
    try {
      // Basic validation and setup
      if (!this.videoId || !this.videoUrl) {
        this.showToast('Sorry, cannot download this video.');
        return;
      }

      // Check if we're running in a native environment with Capacitor
      const isNativeEnvironment = typeof (window as any).Capacitor !== 'undefined' &&
        (window as any).Capacitor.isNativePlatform();

      // For native environments, use Capacitor's Filesystem API
      if (isNativeEnvironment) {
        try {
          // Import necessary Capacitor plugins
          const { Filesystem, Directory } = (window as any).Capacitor.Plugins;

          // First, fetch the video as a blob
          const response = await fetch(this.videoUrl);
          const blob = await response.blob();

          // Convert blob to base64
          const reader = new FileReader();

          // Handle the conversion as a promise
          const base64Data = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64 = reader.result as string;
              // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
              const base64Data = base64.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          // Generate a filename with timestamp to prevent overwriting
          const timestamp = new Date().getTime();
          const fileName = `BlinkPic_${this.videoId}_${timestamp}.jpg`;

          // Save the file to device's download/pictures directory
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.DOCUMENTS,
            recursive: true
          });

          console.log('File saved successfully:', result.uri);
          this.showToast('video downloaded successfully to your documents folder.');
        } catch (error) {
          console.error('Error downloading video in native environment:', error);
          this.showToast('Unable to download the video. Please check app permissions.');
        }
      } else {
        // For browsers, use traditional download approach
        try {
          // Create an anchor element
          const a = document.createElement('a');

          // Set the download attribute and filename
          a.download = `BlinkPic_${this.videoId}.jpg`;

          // If videoUrl is already a blob URL, use it directly
          if (this.videoUrl.startsWith('blob:')) {
            a.href = this.videoUrl;
          }
          // If it's a base64 data URL, use it directly
          else if (this.videoUrl.startsWith('data:')) {
            a.href = this.videoUrl;
          }
          // Otherwise, fetch it first
          else {
            // Fetch the image and create a blob URL
            const response = await fetch(this.videoUrl);
            const blob = await response.blob();
            a.href = URL.createObjectURL(blob);
          }

          // Programmatically click the anchor to trigger the download
          document.body.appendChild(a);
          a.click();

          // Clean up
          document.body.removeChild(a);

          // If we created a blob URL, revoke it to free memory
          if (!this.videoUrl.startsWith('blob:') && !this.videoUrl.startsWith('data:')) {
            URL.revokeObjectURL(a.href);
          }

          console.log('video downloaded successfully in browser');
          this.showToast('video downloaded successfully.');
        } catch (error) {
          console.error('Error downloading video in browser:', error);
          this.showToast('Unable to download the video. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Unexpected error in downloadvideoByThirdParty:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      this.showToast('Unable to download the video. Please try again later.');
    }
  }

  private showToast(message: string): void {
    // Implement your toast logic here, or use the same method you use elsewhere
    // For example:
    // this.toastService.show(message);
  }

  async refreshVideoData() {
    try {
      const videoData = await this._firestoreService.getVideoById(this.videoId);

      if (videoData) {
        // Store video timestamp and adjustments
        this.videoTimestamp = videoData.timestamp;
        this.timeAdjustment = videoData.timeAdjustment || 0;
        this.publisheruserId = videoData.userId;

        // Start or restart the countdown
        this.startCountdown();
      }
    } catch (error) {
      console.error('[videoDetailModal] Error refreshing video data:', error);
    }
  }

  startCountdown() {
    // Clear any existing countdown first
    this.clearCountdown();

    this._firestoreService.getVideoById(this.videoId).then((videoData: any) => {
      if (videoData && videoData.timestamp) {
        let timestamp;
        if (videoData.timestamp.toDate) {
          timestamp = videoData.timestamp.toDate();
        } else if (videoData.timestamp.seconds) {
          timestamp = new Date(videoData.timestamp.seconds * 1000);
        } else {
          timestamp = new Date(videoData.timestamp);
        }

        const timeAdjustment = videoData.timeAdjustment || 0;
        const expirationTime = new Date(timestamp.getTime() + this.expiresIn + (timeAdjustment * 1000));
        const currentTime = new Date();
        const secondsRemaining = Math.max(0, Math.floor((expirationTime.getTime() - currentTime.getTime()) / 1000));

        this.countdown = secondsRemaining;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Execute video deletion when the countdown completes
              this.deleteExpiredVideo();
            }
          });
      } else {
        // Fallback to default countdown
        this.countdown = this.expiresIn / 1000;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Handle completion
              this.timeRemaining = 'Expired';
            }
          });
      }
    }).catch(error => {
      console.error('[VideoDetailModal] Error getting video timestamp:', error);
      // Fallback countdown
      this.countdown = this.expiresIn / 1000;
      this.formatTimeRemaining();
    });
  }

  formatTimeRemaining() {
    if (this.countdown <= 0) {
      this.timeRemaining = 'Expired';
      return;
    }

    const hours = Math.floor(this.countdown / 3600);
    const minutes = Math.floor((this.countdown % 3600) / 60);
    const seconds = this.countdown % 60;

    if (hours > 0) {
      this.timeRemaining = `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    } else if (minutes > 0) {
      this.timeRemaining = `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    } else {
      this.timeRemaining = `${seconds}s`;
    }
  }

  private deleteExpiredVideo() {
    if (this.countdown <= 0) {
      console.log('[videoDetailModal] video expired, deleting video:', this.videoId);

      // First get the original video document
      this._firestoreService.getVideoById(this.videoId)
        .then(videoDoc => {
          // Save the original URL
          const originalUrl = videoDoc?.videoUrl; // Adjust field name if needed

          // Then delete from Firestore
          return this._firestoreService.deleteVideosFromFirestore(this.videoId)
            .then(() => {
              console.log('[videoDetailModal] video deleted from Firestore successfully');

              // Only attempt to delete from Storage if we have a valid URL
              if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
                return this._storageService.deleteVideoFromStorage(originalUrl);
              } else {
                console.log('[videoDetailModal] No Firebase Storage URL found for deletion');
                return Promise.resolve(); // Continue the chain
              }
            });
        })
        .then(() => {
          console.log('[videoDetailModal] video deleted from Storage successfully');
          this.closeModal();
          this._sessionManagementService.performCleanup('reboot');
        })
        .catch(error => {
          console.error('[videoDetailModal] Error deleting expired video:', error);
        });
    }
  }

  closeModal() {
    this.dialogRef.close({
      deleted: false,
      liked: this.likeStatus === 'liked' ? true :
        this.likeStatus === 'disliked' ? false : undefined
    });
  }

  private clearCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  async shareVideo() {
    try {
      // Basic validation and setup
      if (!this.videoId) {
        this.showToast('Sorry, cannot share this video.');
        return;
      }

      const shareTitle = 'A video from BlinkPic';
      let appDeepLink = `https://blinkpic-f7456.web.app/#/location`;

      // Get location data
      try {
        const videoData = await this._firestoreService.getVideoById(this.videoId);

        if (videoData && videoData.location) {
          const latitude = videoData.location.latitude || videoData.location._lat;
          const longitude = videoData.location.longitude || videoData.location._long;

          // Just add the coordinates as query parameters
          appDeepLink += `?lat=${encodeURIComponent(latitude)}&lng=${encodeURIComponent(longitude)}`;
        }
      } catch (error) {
        console.warn('Could not retrieve location data');
      }

      // Create a meaningful description/text for the share with the link
      const shareText = `Check out this video I captured with BlinkPic! ${appDeepLink}`;

      // Check if we're running in a native environment with Capacitor
      const isNativeEnvironment = typeof (window as any).Capacitor !== 'undefined' &&
        (window as any).Capacitor.isNativePlatform();

      // Try to add watermark if we're in a native environment
      let watermarkedVideoUrl = this.videoUrl;
      if (isNativeEnvironment) {
        try {
          const watermarked = await this.addWatermark(this.videoUrl);
          if (watermarked) {
            watermarkedVideoUrl = watermarked;
            console.log('Watermark added successfully');
          }
        } catch (watermarkError) {
          console.warn('Failed to add watermark, using original video:', watermarkError);
          // Continue with original video
        }
      }

      if (isNativeEnvironment) {
        // For native environments, use Capacitor's Share API
        try {
          await Share.share({
            title: shareTitle,
            text: shareText,
            url: watermarkedVideoUrl,
            dialogTitle: 'Share Your Photo' // Only used on Android
          });
          console.log('Photo shared successfully');
        } catch (error) {
          console.error('Error sharing photo:', error);
          this.showToast('Unable to share the photo at this time.');
        }
      } else {
        // For PWA environments, we'll use the Web Share API if available
        if (navigator.share) {
          try {
            // Check if the browser supports file sharing
            const navigatorAny = navigator as any;
            const canShareFiles = navigatorAny.canShare &&
              navigatorAny.canShare({ files: [new File([], 'test.txt')] });

            let shareData: {
              title: string;
              text: string;
              url?: string;
              files?: File[];
            } = {
              title: shareTitle,
              text: shareText,
              url: appDeepLink  // Share the deep link
            };

            if (canShareFiles && watermarkedVideoUrl.startsWith('data:')) {
              try {
                // Convert base64 to blob
                const response = await fetch(watermarkedVideoUrl);
                const blob = await response.blob();
                // Create a File object from the blob
                const file = new File([blob], `video_${this.videoId}.jpg`, { type: 'image/jpeg' });
                // Add the file to share data
                shareData.files = [file];
              } catch (conversionError) {
                console.warn('Could not convert image for sharing:', conversionError);
                // Continue with link-only sharing
              }
            }

            await navigator.share(shareData as ShareData);
            console.log('PWA: video shared successfully');
          } catch (error) {
            console.error('PWA: Error sharing video:', error);
            this.showToast('Unable to share the video. Your browser may not support this feature.');
          }
        } else {
          // Fallback for browsers that don't support Web Share API
          console.warn('Web Share API not supported in this browser');
          this.showToast('Sharing is not supported in this browser. Try using the download button instead.');
        }
      }
    } catch (error) {
      console.error('Unexpected error in shareVideo:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      this.showToast('Unable to share the video. Please try again later.');
    }
  }

  // Keep the watermark function for native environment
  private async addWatermark(imageUrl: string): Promise<string> {
    console.log('[Watermark] Starting watermark process');

    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              reject(new Error('Unable to get canvas context'));
              return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            // Add text watermark
            const watermarkText = 'BlinkPic';
            ctx.font = 'bold 24px Arial';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';

            // Position in bottom right
            const padding = 20;
            const textWidth = ctx.measureText(watermarkText).width;
            const x = canvas.width - textWidth - padding;
            const y = canvas.height - padding;

            ctx.fillText(watermarkText, x, y);
            ctx.strokeText(watermarkText, x, y);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            resolve(dataUrl);
          } catch (canvasError) {
            console.error('[Watermark] Canvas error:', canvasError);
            reject(canvasError);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for watermarking'));
        };

        img.src = imageUrl;
      } catch (error) {
        reject(error);
      }
    });
  }

  async deleteVideo() {
    if (confirm('Are you sure you want to delete this video?')) {
      try {
        console.log('[videoDetailModal] Starting video deletion process');

        // First get the original video document to retrieve the Firebase Storage URL
        const videoDoc = await this._firestoreService.getVideoById(this.videoId);
        const originalUrl = videoDoc?.videoUrl;

        // Delete from Firestore
        await this._firestoreService.deleteVideosFromFirestore(this.videoId);
        console.log('[videoDetailModal] Successfully deleted from Firestore');

        // Try deleting from Storage using the original URL from the database
        if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deleteVideoFromStorage(originalUrl);
          console.log('[videoDetailModal] Successfully deleted from Storage using original URL');
        }
        // Fallback to the current URL if available
        else if (this.videoUrl && this.videoUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deleteVideoFromStorage(this.videoUrl);
          console.log('[videoDetailModal] Successfully deleted from Storage using current URL');
        }
        else {
          console.log('[videoDetailModal] No Firebase Storage URL found for deletion');
        }

        // Close the dialog and pass result back
        await new Promise(resolve => setTimeout(resolve, 100));
        this.dialogRef.close({
          deleted: true,
          videoId: this.videoId
        });
        console.log('[videoDetailModal] Dialog closed successfully');

      } catch (error) {
        console.error('[videoDetailModal] Error in delete process:', error);
        try {
          this.dialogRef.close({
            deleted: true,
            videoId: this.videoId
          });
        } catch (closeError) {
          console.error('[videoDetailModal] Failed to close dialog:', closeError);
        }
      }
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.clearCountdown();

  }
}